import React, { createContext, useContext, useEffect, useState } from 'react';
import { firebaseAuth, isDemoMode, db, functions } from '../firebase';
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut as firebaseSignOut, type User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, setDoc, addDoc, collection, query, where, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { User, UserRole } from '../types';
import { checkIdentity, mapIdentityRole } from '../services/identityService';
import { monitoring } from '../services/monitoringService';
import { Sentry } from '../instrument';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  isDemoMode: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SEED_ADMIN_EMAIL = 'robert@flowcorewater.com';
const INTERNAL_DOMAIN = 'flowcorewater.com';
const demoCurrentUserKey = 'demo_current_user';

/** Internal users default to 'individual', external users default to 'outside_observer' */
const getDefaultRole = (email: string): UserRole => {
  const domain = email.split('@')[1]?.toLowerCase();
  return domain === INTERNAL_DOMAIN ? 'individual' : 'outside_observer';
};

const getDemoUser = (): User => {
  return {
    uid: 'demo-admin',
    email: SEED_ADMIN_EMAIL,
    displayName: 'Robert Demo',
    photoURL: undefined,
    role: 'admin',
    createdAt: new Date(),
    updatedAt: new Date(),
  };
};


const setDemoCurrentUser = (user: User) => {
  localStorage.setItem(demoCurrentUserKey, JSON.stringify(user));
};

const getDemoCurrentUser = (): User | null => {
  const stored = localStorage.getItem(demoCurrentUserKey);
  if (stored) {
    try {
      const data = JSON.parse(stored);
      return {
        ...data,
        createdAt: new Date(data.createdAt),
        updatedAt: new Date(data.updatedAt),
      };
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Call the server-side Cloud Function to resolve duplicate user docs.
 * Runs with admin SDK privileges so it can delete orphan docs, migrate
 * progress/attestations/flashcards, and promote roles — all operations
 * that Firestore security rules block for non-admin client users.
 */
const resolveUserServerSide = async (userData: User): Promise<User> => {
  if (!functions) return userData;
  try {
    const resolve = httpsCallable<Record<string, never>, { merged: boolean; role?: string }>(
      functions,
      'resolveUserOnSignIn'
    );
    const result = await resolve({});
    if (result.data.merged && result.data.role) {
      // Re-read the user doc to get the fully resolved state
      if (db) {
        const freshDoc = await getDoc(doc(db, 'users', userData.uid));
        if (freshDoc.exists()) {
          return { ...freshDoc.data(), uid: userData.uid } as User;
        }
      }
      return { ...userData, role: result.data.role as UserRole };
    }
    return userData;
  } catch (err) {
    console.warn('resolveUserOnSignIn Cloud Function failed (non-fatal):', err);
    return userData;
  }
};

/**
 * After local auth resolves, check the identity service for role/person linkage.
 * Updates the user doc and returns the updated User if identity data is available.
 * Falls back silently if the identity service is unreachable.
 */
const syncWithIdentityService = async (userData: User): Promise<User> => {
  try {
    const identity = await checkIdentity();
    if (!identity) return userData; // Identity service unavailable — keep local data

    const updates: Record<string, unknown> = {};
    let changed = false;

    // Link to identity person if not already linked
    if (identity.personId && userData.identityPersonId !== identity.personId) {
      updates.identityPersonId = identity.personId;
      userData.identityPersonId = identity.personId;
      changed = true;
    }

    // Sync role from identity service if the user has app access
    if (identity.allowed && identity.role) {
      const mappedRole = mapIdentityRole(identity.role, userData.role);
      if (mappedRole !== userData.role) {
        updates.role = mappedRole;
        userData.role = mappedRole;
        changed = true;
      }
    }

    // Persist changes to Firestore
    if (changed && db) {
      updates.updatedAt = serverTimestamp();
      await updateDoc(doc(db, 'users', userData.uid), updates);
    }

    return userData;
  } catch (err) {
    console.warn('Identity sync failed (using local auth data):', err);
    return userData;
  }
};

/** Create a reactivation request when a deactivated user tries to log in */
const createReactivationRequest = async (userId: string, email: string, displayName: string) => {
  if (isDemoMode) {
    const key = 'demo_reactivation_requests';
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    // Don't create duplicate pending requests for same user
    if (existing.some((r: { userId: string; status: string }) => r.userId === userId && r.status === 'pending')) return;
    existing.push({
      id: `req_${Date.now()}`,
      userId,
      userEmail: email,
      userName: displayName,
      requestedAt: new Date().toISOString(),
      status: 'pending',
    });
    localStorage.setItem(key, JSON.stringify(existing));
    return;
  }
  if (!db) return;
  try {
    // Check for existing pending request
    const q = query(collection(db, 'reactivationRequests'), where('userId', '==', userId), where('status', '==', 'pending'));
    const snap = await getDocs(q);
    if (!snap.empty) return; // Already has a pending request
    await addDoc(collection(db, 'reactivationRequests'), {
      userId,
      userEmail: email.toLowerCase(),
      userName: displayName,
      requestedAt: serverTimestamp(),
      status: 'pending',
    });
  } catch (err) {
    console.error('Error creating reactivation request:', err);
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isDemoMode) {
      const demoUser = getDemoCurrentUser() || getDemoUser();
      setUser(demoUser);
      setDemoCurrentUser(demoUser);
      setLoading(false);
      return;
    }

    if (!firebaseAuth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(firebaseAuth, async (firebaseUser: FirebaseUser | null) => {
      try {
        if (firebaseUser) {
          const userDoc = await getDoc(doc(db!, 'users', firebaseUser.uid));

          let userData: User;
          if (userDoc.exists()) {
            userData = { ...userDoc.data(), uid: firebaseUser.uid } as User;

            // Block deactivated users
            if (userData.deactivated) {
              await createReactivationRequest(firebaseUser.uid, userData.email, userData.displayName);
              await firebaseSignOut(firebaseAuth!);
              setUser(null);
              setLoading(false);
              return;
            }
          } else {
            // No doc for this UID — create a minimal one, then let the Cloud Function
            // find and merge any orphaned docs (pending pre-registrations, old UIDs, etc.)
            let role: UserRole = getDefaultRole(firebaseUser.email || '');
            if (firebaseUser.email === SEED_ADMIN_EMAIL) {
              role = 'admin';
            }

            userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || '',
              photoURL: firebaseUser.photoURL || undefined,
              role,
              createdAt: new Date(),
              updatedAt: new Date(),
            };

            await setDoc(doc(db!, 'users', firebaseUser.uid), {
              email: userData.email,
              displayName: userData.displayName,
              photoURL: userData.photoURL,
              role: userData.role,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          }

          // Server-side: merge orphaned duplicates, promote role if needed
          userData = await resolveUserServerSide(userData);

          // Sync with identity service (role + personId linkage)
          userData = await syncWithIdentityService(userData);

          setUser(userData);
          monitoring.setUser({ uid: userData.uid, email: userData.email, role: userData.role });
          Sentry.setUser({ id: userData.uid, email: userData.email, username: userData.displayName });
          monitoring.logAction('auth', 'User authenticated', { uid: userData.uid, role: userData.role });
        } else {
          setUser(null);
          monitoring.setUser(null);
          Sentry.setUser(null);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setUser(null);
        monitoring.setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    if (isDemoMode) {
      const demoUser = getDemoUser();
      setUser(demoUser);
      setDemoCurrentUser(demoUser);
      return;
    }

    if (!firebaseAuth) throw new Error('Firebase not configured');

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(firebaseAuth, provider);

      const userDoc = await getDoc(doc(db!, 'users', result.user.uid));

      let userData: User;
      if (userDoc.exists()) {
        userData = { ...userDoc.data(), uid: result.user.uid } as User;

        // Block deactivated users
        if (userData.deactivated) {
          await createReactivationRequest(result.user.uid, userData.email, userData.displayName);
          await firebaseSignOut(firebaseAuth!);
          throw new Error('This account has been deactivated. Contact your administrator.');
        }
      } else {
        let role: UserRole = getDefaultRole(result.user.email || '');
        if (result.user.email === SEED_ADMIN_EMAIL) {
          role = 'admin';
        }

        userData = {
          uid: result.user.uid,
          email: result.user.email || '',
          displayName: result.user.displayName || '',
          photoURL: result.user.photoURL || undefined,
          role,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        await setDoc(doc(db!, 'users', result.user.uid), {
          email: userData.email,
          displayName: userData.displayName,
          photoURL: userData.photoURL,
          role: userData.role,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      // Server-side: merge orphaned duplicates, promote role if needed
      userData = await resolveUserServerSide(userData);

      // Sync with identity service (role + personId linkage)
      userData = await syncWithIdentityService(userData);

      setUser(userData);
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    if (isDemoMode) {
      setUser(null);
      localStorage.removeItem(demoCurrentUserKey);
      return;
    }

    if (!firebaseAuth) throw new Error('Firebase not configured');

    try {
      await firebaseSignOut(firebaseAuth);
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signInWithGoogle,
    signOut,
    isDemoMode: isDemoMode || !firebaseAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
